import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const Privacy = () => {
  const { currentUser } = useAuth();

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Privacy Policy
            </h1>
            <p className="text-gray-600">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>

          {/* Navigation back */}
          <div className="mb-6">
            <Link
              to={currentUser ? "/dashboard" : "/signup"}
              className="inline-flex items-center text-primary-600 hover:text-primary-800 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {currentUser ? "Back to Dashboard" : "Back to Signup"}
            </Link>
          </div>

          {/* Privacy Content */}
          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 mb-4">
                MyDigiSign ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our digital signage management platform and related services.
              </p>
              <p className="text-gray-700 mb-4">
                By using our Service, you consent to the data practices described in this policy. If you do not agree with our policies and practices, please do not use our Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Personal Information</h3>
              <p className="text-gray-700 mb-4">
                We may collect personal information that you provide directly to us, including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Name and contact information (email address, phone number)</li>
                <li>Account credentials and profile information</li>
                <li>Company or organization details</li>
                <li>Communication preferences</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.2 Usage Information</h3>
              <p className="text-gray-700 mb-4">
                We automatically collect certain information about your use of the Service, including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Device information (IP address, browser type, operating system)</li>
                <li>Usage patterns and analytics data</li>
                <li>Content creation and management activities</li>
                <li>Device connection and performance metrics</li>
                <li>Error logs and system diagnostics</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.3 Content and Media</h3>
              <p className="text-gray-700 mb-4">
                We store and process content you upload to the Service, including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Images, videos, and multimedia files</li>
                <li>Schedule and playlist configurations</li>
                <li>Device settings and configurations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">
                We use the information we collect for various purposes, including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Providing and maintaining the Service</li>
                <li>Personalizing your experience and improving our services</li>
                <li>Communicating with you about updates, security, and support</li>
                <li>Analyzing usage patterns to enhance functionality</li>
                <li>Ensuring compliance with legal obligations</li>
                <li>Preventing fraud and maintaining security</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Information Sharing and Disclosure</h2>
              <p className="text-gray-700 mb-4">
                We do not sell, trade, or otherwise transfer your personal information to third parties except in the following circumstances:
              </p>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.1 Service Providers</h3>
              <p className="text-gray-700 mb-4">
                We may share information with trusted third-party service providers who assist us in operating our Service, including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Cloud hosting and storage providers</li>
                <li>Analytics and monitoring tools</li>
                <li>Customer support platforms</li>
                <li>Email and communication services</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.2 Legal Requirements</h3>
              <p className="text-gray-700 mb-4">
                We may disclose your information if required by law or in response to valid legal requests, such as subpoenas or court orders.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.3 Business Transfers</h3>
              <p className="text-gray-700 mb-4">
                In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the transaction.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">4.4 Consent</h3>
              <p className="text-gray-700 mb-4">
                We may share your information with your explicit consent or as otherwise described in this policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Security</h2>
              <p className="text-gray-700 mb-4">
                We implement appropriate technical and organizational security measures to protect your information against unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Encryption of data in transit and at rest</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and authentication mechanisms</li>
                <li>Secure data centers and infrastructure</li>
                <li>Employee training on data protection</li>
              </ul>
              <p className="text-gray-700 mb-4">
                However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
              <p className="text-gray-700 mb-4">
                We retain your information for as long as necessary to provide the Service and fulfill the purposes outlined in this policy. When you delete your account, all your data will be deleted immediately from our systems.
              </p>
              <p className="text-gray-700 mb-4">
                Specific retention periods for active accounts include:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Account information: Retained while your account is active</li>
                <li>Content and media: Retained according to your account settings and subscription plan</li>
                <li>Usage analytics: Retained for up to 2 years for service improvement purposes</li>
                <li>Communication records: Retained for customer support and legal compliance</li>
              </ul>
              <p className="text-gray-700 mb-4">
                Note: There is no account deactivation or suspension in our system. When you delete your account, all associated data is permanently removed immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Your Rights and Choices</h2>
              <p className="text-gray-700 mb-4">
                Depending on your location, you may have certain rights regarding your personal information:
              </p>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">7.1 Access and Portability</h3>
              <p className="text-gray-700 mb-4">
                You can access and download your personal information through your account settings or by contacting us.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">7.2 Correction and Updates</h3>
              <p className="text-gray-700 mb-4">
                You can update your account information and preferences through your account settings.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">7.3 Deletion</h3>
              <p className="text-gray-700 mb-4">
                You can request deletion of your account and associated data, subject to legal and contractual obligations.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">7.4 Opt-out</h3>
              <p className="text-gray-700 mb-4">
                You can opt out of certain communications and data processing activities through your account settings.
              </p>


            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. International Data Transfers</h2>
              <p className="text-gray-700 mb-4">
                Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your information in accordance with applicable data protection laws.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Children's Privacy</h2>
              <p className="text-gray-700 mb-4">
                Our Service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Third-Party Services</h2>
              <p className="text-gray-700 mb-4">
                Our Service may contain links to third-party websites or integrate with third-party services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies before providing any information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Changes to This Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date. Your continued use of the Service after such changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>Email:</strong> info@mydigisign.ai<br />
                  <strong>Phone:</strong> +1 647 286 7949
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Regional Privacy Rights</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">13.1 California Privacy Rights (CCPA)</h3>
              <p className="text-gray-700 mb-4">
                California residents have additional rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information is collected and how it is used, the right to delete personal information, and the right to opt out of the sale of personal information.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">13.2 European Privacy Rights (GDPR)</h3>
              <p className="text-gray-700 mb-4">
                European Union residents have rights under the General Data Protection Regulation (GDPR), including the right to access, rectification, erasure, data portability, and the right to object to processing.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">13.3 Other Jurisdictions</h3>
              <p className="text-gray-700 mb-4">
                Residents of other jurisdictions may have additional privacy rights under applicable laws. Please contact us if you need information about rights specific to your location.
              </p>
            </section>
          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-gray-200 text-center">
            <p className="text-gray-600 text-sm">
              This Privacy Policy is effective as of the date listed above and applies to all users of the MyDigiSign Service.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy; 